import {
	Camera,
	Check,
	Clock,
	ImageIcon,
	Send,
	UserRound,
	X,
} from "lucide-react";
import { motion } from "motion/react";
import {
	BrowserFrame,
	ExampleTag,
	PhoneFrame,
} from "@/components/blocks/frames/Frames";
import { EASE } from "@/components/motion/motion";
import { useI18n } from "@/lib/i18n/use-i18n";
import { cn } from "@/lib/utils";
import {
	formatPercent,
	StageScene,
	stageBar,
	stageDraw,
	stageItem,
	stagePop,
	stageRise,
	type UseCaseSceneProps,
} from "./StageScene";

/** Control, score, non-conformity, client portal. Server renders the score. */
const DURATIONS = [4000, 3800, 4200, 4000] as const;

/** The supplies criterion fails in the sample control. */
const FAILED = "supplies";

/** Sample scores: the site, then each zone (the sanitary zone falls short). */
const SITE_SCORE = 94;
export const THRESHOLD = 90;
const ZONE_SCORES = {
	reception: 98,
	office: 96,
	sanitary: 82,
	cafeteria: 95,
} as const;

/** Twelve monthly scores per site (sample data), sparklines and trend chart. */
export const TRENDS = {
	a: [91, 92, 90, 93, 94, 92, 95, 94, 93, 95, 94, 94],
	b: [95, 96, 96, 97, 95, 97, 98, 97, 96, 97, 98, 97],
	c: [93, 91, 92, 90, 91, 89, 90, 92, 90, 88, 90, 89],
} as const;

const RING = { radius: 42, stroke: 9 } as const;

const sparkPath = (values: readonly number[]) =>
	values
		.map(
			(value, index) =>
				`${index === 0 ? "M" : "L"}${(index / (values.length - 1)) * 100} ${30 - ((value - 85) / 15) * 30}`,
		)
		.join(" ");

/**
 * Commercial cleaning: a zone checked against the contract's own grid, the
 * score computed on the spot, a non-conformity assigned and closed with a
 * photo, and the client following every site's quality.
 */
export function CleaningScene(props: UseCaseSceneProps) {
	const { dict, locale } = useI18n();
	const scene = dict.useCases.items.cleaning.scene;
	const tag = dict.common.exampleBadge;

	const steps = [
		<PhoneFrame key="control" className="h-full w-auto max-w-none">
			<div className="flex h-full flex-col gap-2 px-3.5 pt-1 pb-3">
				<ExampleTag className="self-start px-1.5 text-[8px]">{tag}</ExampleTag>
				<p className="text-[12px] leading-tight font-semibold">
					{scene.control.title}
				</p>
				<p className="text-[9.5px] text-muted-foreground">
					{scene.control.zone}
				</p>
				<ul className="mt-1 space-y-1.5">
					{Object.entries(scene.control.criteria).map(([key, label], index) => {
						const failed = key === FAILED;
						return (
							<motion.li
								key={key}
								variants={stageItem}
								custom={index}
								className={cn(
									"flex items-center gap-2 rounded-lg border px-2 py-1.5 text-[10px]",
									failed && "border-foreground/40",
								)}
							>
								<span className="min-w-0 flex-1 truncate">{label}</span>
								<motion.span
									variants={stagePop}
									custom={0.5 + index * 0.18}
									className={cn(
										"flex size-4 shrink-0 items-center justify-center rounded-full",
										failed
											? "bg-foreground text-background"
											: "bg-signal text-signal-foreground",
									)}
								>
									{failed ? (
										<X className="size-2.5" strokeWidth={3} />
									) : (
										<Check className="size-2.5" strokeWidth={3} />
									)}
								</motion.span>
							</motion.li>
						);
					})}
				</ul>
				<motion.div
					variants={stageRise}
					custom={6}
					className="flex items-center gap-2 rounded-lg border border-dashed px-2 py-1.5 text-[9.5px] text-muted-foreground"
				>
					<span className="dot-grid flex size-7 items-center justify-center rounded-md bg-muted">
						<Camera className="size-3" />
					</span>
					{scene.control.photo}
				</motion.div>
				<div className="mt-auto flex gap-1.5 text-[9.5px] font-semibold">
					<span className="flex h-7 flex-1 items-center justify-center rounded-lg bg-foreground text-background">
						{scene.control.next}
					</span>
				</div>
			</div>
		</PhoneFrame>,

		<PhoneFrame key="score" className="h-full w-auto max-w-none">
			<div className="flex h-full flex-col gap-2 px-3.5 pt-1 pb-3">
				<ExampleTag className="self-start px-1.5 text-[8px]">{tag}</ExampleTag>
				<p className="text-[12px] leading-tight font-semibold">
					{scene.score.title}
				</p>
				<p className="text-[9.5px] text-muted-foreground">{scene.score.site}</p>
				<div className="relative mx-auto my-1 size-28">
					<svg
						aria-hidden="true"
						viewBox="0 0 100 100"
						className="size-full -rotate-90"
					>
						<circle
							cx="50"
							cy="50"
							r={RING.radius}
							fill="none"
							className="stroke-foreground/[0.08]"
							strokeWidth={RING.stroke}
						/>
						<motion.circle
							cx="50"
							cy="50"
							r={RING.radius}
							fill="none"
							className="stroke-signal"
							strokeWidth={RING.stroke}
							strokeLinecap="round"
							variants={{
								hidden: { pathLength: 0 },
								visible: {
									pathLength: SITE_SCORE / 100,
									transition: { duration: 1.3, ease: EASE, delay: 0.2 },
								},
							}}
						/>
					</svg>
					<span className="absolute inset-0 flex flex-col items-center justify-center">
						<span className="font-mono text-2xl font-semibold tracking-tight">
							{formatPercent(SITE_SCORE, locale)}
						</span>
						<span className="text-[8px] text-muted-foreground">
							{scene.score.threshold}
						</span>
					</span>
				</div>
				<ul className="space-y-1.5">
					{Object.entries(scene.score.zones).map(([key, label], index) => {
						const value = ZONE_SCORES[key as keyof typeof ZONE_SCORES];
						const below = value < THRESHOLD;
						return (
							<li key={key} className="text-[9.5px]">
								<div className="flex justify-between gap-2">
									<span className="truncate">{label}</span>
									<span
										className={cn(
											"font-mono font-medium",
											below && "rounded bg-foreground px-1 text-background",
										)}
									>
										{formatPercent(value, locale)}
									</span>
								</div>
								<span className="relative mt-1 block h-1 overflow-hidden rounded-full bg-foreground/[0.07]">
									<motion.span
										variants={stageBar}
										custom={index}
										className={cn(
											"absolute inset-y-0 left-0 origin-left rounded-full",
											below ? "bg-foreground/60" : "bg-signal",
										)}
										style={{ width: `${formatPercent(value, locale)}` }}
									/>
									<span
										className="absolute inset-y-0 w-px bg-foreground/50"
										style={{ left: `${THRESHOLD}%` }}
									/>
								</span>
							</li>
						);
					})}
				</ul>
			</div>
		</PhoneFrame>,

		<BrowserFrame
			key="fix"
			host={scene.host}
			tag={tag}
			className="w-full"
			contentClassName="p-4"
		>
			<p className="font-mono text-[10px] text-muted-foreground">
				{scene.fix.title}
			</p>
			<div className="mt-2 grid grid-cols-[minmax(0,0.8fr)_minmax(0,1.2fr)] gap-4">
				<div className="space-y-2">
					<div className="dot-grid relative flex aspect-[4/3] items-center justify-center rounded-lg bg-muted">
						<ImageIcon className="size-5 text-muted-foreground" />
						<span className="absolute bottom-1.5 left-1.5 rounded bg-background/90 px-1 font-mono text-[8px]">
							{scene.fix.before}
						</span>
					</div>
					<motion.div
						variants={stageRise}
						custom={4}
						className="dot-grid relative flex aspect-[4/3] items-center justify-center rounded-lg border-2 border-signal bg-signal/10"
					>
						<Check className="size-5 text-signal-ink" />
						<span className="absolute bottom-1.5 left-1.5 rounded bg-background/90 px-1 font-mono text-[8px]">
							{scene.fix.after}
						</span>
					</motion.div>
				</div>
				<div className="flex flex-col gap-2 text-[10px]">
					<p className="text-[12px] leading-tight font-semibold">
						{scene.fix.text}
					</p>
					<p className="text-muted-foreground">{scene.fix.zone}</p>
					{(
						[
							[UserRound, scene.fix.owner],
							[Clock, scene.fix.due],
							[Send, scene.fix.action],
						] as const
					).map(([Icon, text], index) => (
						<motion.p
							key={text}
							variants={stageItem}
							custom={index + 1}
							className="flex items-start gap-2 rounded-lg border px-2 py-1.5"
						>
							<Icon className="mt-px size-3 shrink-0 text-muted-foreground" />
							<span className="leading-snug">{text}</span>
						</motion.p>
					))}
					<motion.span
						variants={stagePop}
						custom={1.9}
						className="mt-auto flex w-fit items-center gap-1 rounded-full bg-signal px-2.5 py-1 text-[9.5px] font-semibold text-signal-foreground"
					>
						<Check className="size-3" strokeWidth={3} />
						{scene.fix.lifted}
					</motion.span>
				</div>
			</div>
		</BrowserFrame>,

		<BrowserFrame
			key="portal"
			host={scene.host}
			tag={tag}
			className="w-full"
			contentClassName="p-4"
		>
			<div className="flex items-end justify-between gap-3">
				<div className="min-w-0">
					<p className="truncate text-sm font-semibold">{scene.portal.title}</p>
					<p className="text-[10px] text-muted-foreground">
						{scene.portal.period}
					</p>
				</div>
				<span className="shrink-0 font-mono text-[9px] text-muted-foreground">
					{scene.portal.threshold}
				</span>
			</div>
			<ul className="mt-3 space-y-2">
				{Object.entries(scene.portal.sites).map(([key, site], index) => {
					const trend = TRENDS[key as keyof typeof TRENDS];
					const last = trend[trend.length - 1] ?? 0;
					const below = last < THRESHOLD;
					return (
						<motion.li
							key={key}
							variants={stageItem}
							custom={index}
							className="grid grid-cols-[minmax(0,1fr)_5.5rem_2.75rem] items-center gap-3 rounded-lg border bg-background px-3 py-2"
						>
							<span className="truncate text-[10.5px] font-medium">{site}</span>
							<svg
								aria-hidden="true"
								viewBox="0 0 100 30"
								preserveAspectRatio="none"
								className="h-6 w-full overflow-visible"
							>
								<line
									x1="0"
									x2="100"
									y1={30 - ((THRESHOLD - 85) / 15) * 30}
									y2={30 - ((THRESHOLD - 85) / 15) * 30}
									className="stroke-foreground/25"
									strokeDasharray="2 3"
									vectorEffect="non-scaling-stroke"
								/>
								<motion.path
									d={sparkPath(trend)}
									fill="none"
									className={below ? "stroke-foreground/60" : "stroke-signal"}
									strokeWidth={1.6}
									strokeLinejoin="round"
									vectorEffect="non-scaling-stroke"
									variants={stageDraw}
									custom={0.2 + index * 0.2}
								/>
							</svg>
							<span
								className={cn(
									"text-right font-mono text-[11px] font-semibold",
									below && "text-muted-foreground",
								)}
							>
								{formatPercent(last, locale)}
							</span>
						</motion.li>
					);
				})}
			</ul>
			<motion.div
				variants={stageRise}
				custom={4}
				className="mt-3 flex items-center justify-between gap-3 rounded-lg bg-muted/70 px-3 py-2 text-[10px]"
			>
				<span className="font-medium">{scene.portal.report}</span>
				<span className="font-mono text-muted-foreground">
					{scene.portal.open}
				</span>
			</motion.div>
		</BrowserFrame>,
	];

	return (
		<StageScene
			{...props}
			labels={Object.values(scene.labels)}
			durations={DURATIONS}
			initial={1}
			render={(step) => steps[step]}
		/>
	);
}
